import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar.jsx';
import Topbar from './Topbar.jsx';
import ChatNotificationsMonitor from './ChatNotificationsMonitor.jsx';
import { ChatUnreadProvider } from './ChatUnreadProvider.jsx';
import { useUI } from '../../context/UIContext.jsx';
import { isOperatorCompactPath } from '@/utils/operatorCompact.js';

export default function AppShell({ children }) {
  const location = useLocation();
  const {
    isCompactViewport,
    isSidebarPinned,
    isSidebarHoverOpen,
    openSidebarHover,
    closeSidebarHover,
    toggleSidebar,
    sidebarExpandedWidth,
  } = useUI();

  const isAccountStatementView = ['/ctasctes/cuenta-corriente-clientes/resumen-de-cuenta', '/ctasctes/cuenta-corriente-clientes/deuda-de-clientes'].includes(location.pathname);
  const isFullWidthMonitor = location.pathname === '/monitor/operaciones';
  const isEmissionView = location.pathname === '/guias/emitir' || location.pathname === '/fojas/emision';
  const isCargasView = location.pathname.startsWith('/cargas');
  const isChatView = location.pathname === '/chat';
  const isOperatorCompactView = isOperatorCompactPath(location.pathname);
  const showPinnedSidebar = !isCompactViewport && isSidebarPinned;
  const allowOverlaySidebar = isCompactViewport || !showPinnedSidebar;
  const showOverlaySidebar = allowOverlaySidebar && isSidebarHoverOpen;

  const contentClassName = isAccountStatementView
    ? 'w-full min-w-0 px-3 pb-4 pt-2 md:px-4'
    : isFullWidthMonitor
    ? 'h-full w-full overflow-hidden px-3 pb-[calc(var(--page-bottom-safe)+1.25rem)] pt-3 md:px-4 md:pt-4'
    : isEmissionView
      ? 'h-full w-full overflow-hidden px-2 pb-[calc(var(--page-bottom-safe)+0.75rem)] pt-2 md:px-3 md:pt-3 xl:px-4'
      : isOperatorCompactView
        ? 'mx-auto w-full max-w-[var(--content-max)] px-[var(--page-gutter)] pb-[calc(var(--page-bottom-safe)+0.5rem)] pt-1.5 md:pt-2'
      : isCargasView
        ? 'mx-auto h-full w-full max-w-[var(--content-max)] overflow-hidden px-[var(--page-gutter)] pb-[calc(var(--page-bottom-safe)+0.75rem)] pt-3 md:pt-4'
        : isChatView
          ? 'mx-auto flex h-full min-h-0 w-full max-w-[var(--content-max)] flex-col overflow-hidden px-[var(--page-gutter)] pb-2 pt-2 md:pt-3'
        : 'mx-auto w-full max-w-[var(--content-max)] px-[var(--page-gutter)] pb-[calc(var(--page-bottom-safe)+1.25rem)] pt-3 md:pt-4';

  const railHandlers = allowOverlaySidebar
    ? {
        onMouseEnter: openSidebarHover,
        onFocusCapture: openSidebarHover,
      }
    : {};

  return (
    <ChatUnreadProvider>
      <div className={`${isChatView ? 'h-screen overflow-hidden' : 'min-h-screen'} bg-[rgb(var(--bg))] text-[rgb(var(--fg))]`}>
        <ChatNotificationsMonitor />
      {showOverlaySidebar && isCompactViewport ? (
        <div
          className="fixed inset-0 z-[calc(var(--z-sidebar)-1)] bg-[rgba(var(--shadow),0.3)] transition-opacity duration-200"
          onClick={closeSidebarHover}
          aria-hidden="true"
        />
      ) : null}

      <div className={`flex ${isChatView ? 'h-full' : 'min-h-screen'}`}>
        <div
          className="relative shrink-0"
          style={{ width: isCompactViewport ? '0px' : (showPinnedSidebar ? `${sidebarExpandedWidth}px` : 'var(--sidebar-w-collapsed)') }}
          {...railHandlers}
        >
          {!isCompactViewport ? <Sidebar
            collapsed={!showPinnedSidebar}
            onToggle={toggleSidebar}
            showLogoutShortcut
            compact
          /> : null}
        </div>

        {showOverlaySidebar ? (
          <div
            className="fixed left-0 top-0 bottom-0 z-[var(--z-sidebar)]"
            style={{ width: `${sidebarExpandedWidth}px` }}
            onMouseLeave={closeSidebarHover}
            onBlurCapture={event => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                closeSidebarHover();
              }
            }}
          >
            <Sidebar
              collapsed={false}
              onToggle={!isCompactViewport ? toggleSidebar : undefined}
              showLogoutShortcut
              compact
              overlay
            />
          </div>
        ) : null}

        <div className={`flex min-w-0 flex-1 flex-col ${isChatView ? 'h-full' : 'min-h-screen'}`}>
          <Topbar />
          <main className={`min-h-0 flex-1 ${contentClassName}`}>
            {children}
          </main>
        </div>
      </div>
      </div>
    </ChatUnreadProvider>
  );
}
